import { ApolloLink } from 'apollo-link';
import { createHttpLink } from 'apollo-link-http';
import { InMemoryCache } from 'apollo-cache-inmemory';
import { ApolloClient } from 'apollo-client';
import { onError } from 'apollo-link-error';
import { RetryLink } from 'apollo-link-retry';

import * as Sentry from '@sentry/vue'; // Assuming Sentry is set up similarly

const csrfToken = document.querySelector('meta[name=csrf-token]')?.getAttribute('content');

const httpLink = createHttpLink({
  uri: '/graphql',
  headers: {
    'X-CSRF-Token': csrfToken,
  },
});

const errorLink = onError(({
  graphQLErrors, networkError, operation, forward, response,
}) => {
  if (graphQLErrors) {
    graphQLErrors.forEach((error) => {
      Sentry.withScope((scope) => {
        scope.setExtras({
          response: response || 'No response available',
          locations: error.locations,
          path: error.path,
          operationName: operation?.operationName,
          variables: operation?.variables,
        });
        Sentry.captureException(new Error(`[GraphQL error]: Message: ${error.message}`));
      });
    });
  }
  if (networkError) {
    Sentry.withScope((scope) => {
      scope.setExtras({
        networkError,
        response: response || 'No response available',
        operationName: operation?.operationName,
        variables: operation?.variables,
        message: networkError?.message,
      });
      Sentry.captureException(networkError);
    });

    forward(operation);
  }
});

const retryLink = new RetryLink({
  attempts: (count, error) => !!error && count < 3,
  delay: count => count * 1000,
});

// Strip __typename from variables
const cleanTypenameLink = new ApolloLink((operation, forward) => {
  if (operation.variables) {
    const omitTypename = (key, value) => (key === '__typename' ? undefined : value);
    operation.variables = JSON.parse(JSON.stringify(operation.variables), omitTypename);
  }

  return forward(operation);
});

const cache = new InMemoryCache({});

// Create the apollo client
const apolloClient = new ApolloClient({
  link: ApolloLink.from([
    retryLink,
    errorLink,
    cleanTypenameLink,
    httpLink,
  ]),
  cache,
});

export default apolloClient;
